// src/Sidebar.js
import React, { useEffect, useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { supabase } from "./supabaseClient";

export default function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, credits, signOut: signOutFromAuth } = useAuth();

  const [games, setGames] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [mode, setMode] = useState("dev"); // "dev" | "tester"
  const [subscription, setSubscription] = useState(null);
  const [maxGames, setMaxGames] = useState(null);

  // Keep mode in sync with route
  useEffect(() => {
    if (location.pathname.startsWith("/dashboard/tester")) {
      setMode("tester");
    } else {
      setMode("dev");
    }
  }, [location.pathname]);

  // Close sidebar on route change (mobile)
  useEffect(() => {
    if (window.innerWidth < 768) {
      setSidebarOpen(false);
    }
  }, [location]);

  // Escape key closes sidebar (mobile)
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") setSidebarOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  // Load games for this owner
  useEffect(() => {
    const loadGames = async () => {
      if (!currentUser) {
        setGames([]);
        return;
      }
      try {
        const { data, error } = await supabase
          .from("games")
          .select("*")
          .eq("owner_id", currentUser.id)
          .order("created_at", { ascending: true });

        if (error) {
          console.error("Error loading games in sidebar:", error);
        } else {
          setGames(data || []);
        }
      } catch (e) {
        console.error("Unexpected error loading games in sidebar:", e);
      }
    };

    loadGames();
  }, [currentUser, location.pathname]);

  // Load subscription info to determine max games
  useEffect(() => {
    const loadSubscription = async () => {
      if (!currentUser) {
        setSubscription(null);
        setMaxGames(null);
        return;
      }
      try {
        const { data, error } = await supabase
          .from("subscriptions")
          .select(
            `
            plan_code,
            subscription_plans ( max_games )
          `
          )
          .eq("user_id", currentUser.id)
          .maybeSingle();

        if (error) {
          console.error("Error loading subscription in sidebar:", error);
          setMaxGames(1); // Default to 1 game for free tier
        } else if (data) {
          setSubscription(data);
          setMaxGames(data.subscription_plans?.max_games || 1);
        } else {
          // No subscription record = free tier
          setMaxGames(1);
        }
      } catch (e) {
        console.error("Unexpected error loading subscription in sidebar:", e);
        setMaxGames(1); // Default to 1 game for free tier
      }
    };

    loadSubscription();
  }, [currentUser]);

  const handleSignOut = async () => {
    try {
      await signOutFromAuth();
    } catch (error) {
      console.error("Error signing out", error);
    }
  };

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  // Mode toggle navigation
  const handleModeClick = (value) => {
    if (value === mode) return;
    setMode(value);
    if (value === "dev") {
      navigate("/dashboard");
    } else {
      navigate("/dashboard/tester");
    }
  };

  // Current selected game from query param (for highlighting)
  const search = new URLSearchParams(location.search);
  const selectedGameId = search.get("game");

  // Simple helper for avatar fallback
  const getInitial = (user) =>
    user?.displayName?.trim()?.charAt(0)?.toUpperCase() || "?";

  return (
    <>
      {/* Mobile open button */}
      <button
        onClick={toggleSidebar}
        className="fixed top-4 left-4 z-40 rounded-lg bg-[#1f1f1f] border border-[#444] p-2 text-[#eee] md:hidden shadow-sm"
        aria-label="Toggle sidebar"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          className="w-5 h-5"
        >
          <path
            fillRule="evenodd"
            d="M2 4.75A.75.75 0 0 1 2.75 4h14.5a.75.75 0 0 1 0 1.5H2.75A.75.75 0 0 1 2 4.75ZM2 10a.75.75 0 0 1 .75-.75h14.5a.75.75 0 0 1 0 1.5H2.75A.75.75 0 0 1 2 10Zm0 5.25a.75.75 0 0 1 .75-.75h14.5a.75.75 0 0 1 0 1.5H2.75a.75.75 0 0 1-.75-.75Z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 z-50 h-[100dvh] w-[280px]
          flex flex-col bg-[#181818] text-[#eee] border-r border-[#333] shadow-lg
          transform transition-transform duration-300
          ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}
          md:translate-x-0
        `}
      >
        {/* Top bar: logo + mode switch */}
        <div className="px-6 pt-6 pb-6 flex flex-col items-start justify-between gap-y-3">
          <Link to="/dashboard" className="flex items-center gap-x-2">
            <h1 className="text-2xl font-bold font-bebas tracking-wider">
              Playset
            </h1>
          </Link>

          {/* Dev / Tester segmented control */}
          <div className="inline-flex items-center rounded-xl bg-[#111]/80 border border-[#444] p-0.5 font-nunito text-sm w-full">
            <button
              type="button"
              onClick={() => handleModeClick("dev")}
              className={`flex-1 px-3 py-1 rounded-lg transition ${mode === "dev"
                ? "bg-[#8338ec] text-white shadow-sm"
                : "text-gray-300 hover:bg-white/5"
                }`}
            >
              Developer
            </button>
            <button
              type="button"
              onClick={() => handleModeClick("tester")}
              className={`flex-1 px-3 py-1 rounded-lg transition ${mode === "tester"
                ? "bg-[#8338ec] text-white shadow-sm"
                : "text-gray-300 hover:bg-white/5"
                }`}
            >
              Tester
            </button>
          </div>
        </div>

        <div className="h-px w-full bg-white/10" />

        {/* Scrollable middle section */}
        <div className="flex-1 overflow-y-auto custom-scrollbar px-6 pt-6 pb-4">
          {/* (Optional) credits or usage pill – only if you want it visible */}
          {typeof credits !== "undefined" && (
            <div className="mb-5">
              <div className="inline-flex items-center rounded-full bg-[#222] border border-[#444] px-3 py-1 text-[11px] font-nunito text-gray-300 gap-2">
                <span className="h-2 w-2 rounded-full bg-green-400" />
                <span>Playtest time available</span>
                <span className="font-semibold text-[#facc15]">
                  {credits}
                </span>
              </div>
            </div>
          )}

          {/* My Games header */}
          <div className="flex items-center justify-between mb-3">
            <div className="font-nunito text-sm font-light tracking-wide text-gray-100 uppercase">
              My games
            </div>
            <button
              onClick={() => navigate("/dashboard/games/new")}
              className="flex items-center justify-center text-xs px-2.5 py-1.5 font-nunito rounded-lg border border-[#8338ec] bg-[#8338ec]/15 hover:bg-[#8338ec] hover:text-white text-[#f5f5f5] transition"
            >
              Add new
            </button>
          </div>

          {/* Games list */}
          <div className="mt-2 space-y-1.5">
            {games.length === 0 ? (
              <div className="text-xs text-gray-400 font-nunito bg-[#101010] border border-dashed border-[#333] rounded-lg px-3 py-3">
                No games yet. Create one to start testing.
              </div>
            ) : (
              games.map((g, index) => {
                const isActive = selectedGameId === g.id;
                const isDisabled = maxGames !== null && index >= maxGames;
                
                return (
                  <button
                    key={g.id}
                    onClick={() => !isDisabled && navigate(`/dashboard?game=${g.id}`)}
                    disabled={isDisabled}
                    className={`
                      w-full flex items-center gap-x-3 rounded-lg pr-3 py-2 text-left
                      border transition-all duration-150 ease-out
                      ${isDisabled
                        ? "opacity-50 cursor-not-allowed bg-transparent border-transparent pl-0"
                        : isActive
                        ? "bg-[#222] border-transparent shadow-sm pl-3"
                        : "bg-transparent border-transparent pl-0 hover:bg-[#222] hover:pl-3"
                      }
                    `}
                    title={isDisabled ? "Upgrade your plan to access this game" : undefined}
                  >
                    <div className="flex flex-col">
                      <div className={`font-nunito text-sm truncate ${isDisabled ? "text-gray-500" : "text-gray-50"}`}>
                        {g.name}
                        {isDisabled && <span className="text-[10px] ml-1 text-gray-600">(disabled)</span>}
                      </div>
                      {g.developer_name && (
                        <div className={`text-[11px] truncate ${isDisabled ? "text-gray-600" : "text-gray-400"}`}>
                          by {g.developer_name}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Bottom section – CTA + actions + user */}
        <div className="px-6 pb-6 pt-6 border-t border-[#222] bg-[#151515]">
          {/* Get playtest time CTA */}
          <Link to="purchase/">
            <button className="w-full mb-5 text-sm px-3 py-2 font-nunito font-semibold rounded-lg border border-yellow-500 bg-yellow-500/15 hover:bg-yellow-500 hover:text-black text-yellow-100 transition flex items-center justify-center gap-2">
              <span>Get playtest time</span>
            </button>
          </Link>

          {/* Action row */}
          <div className="flex flex-row justify-between items-center mb-5 text-sm text-gray-200">
            {/* Settings */}
            <button
              onClick={() => navigate("/dashboard/settings")}
              className="relative inline-flex items-center justify-center group"
              aria-label="Settings"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="w-5 h-5"
              >
                <path
                  fillRule="evenodd"
                  d="M11.078 2.25c-.917 0-1.699.663-1.85 1.567L9.05 4.889c-.02.12-.115.26-.297.348a7.493 7.493 0 0 0-.986.57c-.166.115-.334.126-.45.083L6.3 5.508a1.875 1.875 0 0 0-2.282.819l-.922 1.597a1.875 1.875 0 0 0 .432 2.385l.84.692c.095.078.17.229.154.43a7.598 7.598 0 0 0 0 1.139c.015.2-.59.352-.153.43l-.841.692a1.875 1.875 0 0 0-.432 2.385l.922 1.597a1.875 1.875 0 0 0 2.282.818l1.019-.382c.115-.043.283-.031.45.082.312.214.641.405.985.57.182.088.277.228.297.35l.178 1.071c.151.904.933 1.567 1.85 1.567h1.844c.916 0 1.699-.663 1.85-1.567l.178-1.072c.02-.12.114-.26.297-.349.344-.165.673-.356.985-.57.167-.114.335-.125.45-.082l1.02.382a1.875 1.875 0 0 0 2.28-.819l.923-1.597a1.875 1.875 0 0 0-.432-2.385l-.84-.692c-.095-.078-.17-.229-.154-.43a7.614 7.614 0 0 0 0-1.139c-.016-.2.059-.352.153-.43l.84-.692c.708-.582.891-1.59.433-2.385l-.922-1.597a1.875 1.875 0 0 0-2.282-.818l-1.02.382c-.114.043-.282.031-.449-.083a7.49 7.49 0 0 0-.985-.57c-.183-.087-.277-.227-.297-.348l-.179-1.072a1.875 1.875 0 0 0-1.85-1.567h-1.843ZM12 15.75a3.75 3.75 0 1 0 0-7.5 3.75 3.75 0 0 0 0 7.5Z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 rounded-md bg-[#fefefe] text-[#1f1f1f] text-xs font-nunito whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
                Settings
              </span>
            </button>

            {/* Support */}
            <button
              onClick={() => navigate("/dashboard/support")}
              className="relative inline-flex items-center justify-center group"
              aria-label="Support"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="w-5 h-5"
              >
                <path
                  fillRule="evenodd"
                  d="M4.804 21.644A6.707 6.707 0 0 0 6 21.75a6.721 6.721 0 0 0 3.583-1.029c.774.182 1.584.279 2.417.279 5.322 0 9.75-3.97 9.75-9 0-5.03-4.428-9-9.75-9s-9.75 3.97-9.75 9c0 2.409 1.025 4.587 2.674 6.192.232.226.277.428.254.543a3.73 3.73 0 0 1-.814 1.686.75.75 0 0 0 .44 1.223ZM8.25 10.875a1.125 1.125 0 1 0 0 2.25 1.125 1.125 0 0 0 0-2.25ZM10.875 12a1.125 1.125 0 1 1 2.25 0 1.125 1.125 0 0 1-2.25 0Zm4.875-1.125a1.125 1.125 0 1 0 0 2.25 1.125 1.125 0 0 0 0-2.25Z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 rounded-md bg-[#fefefe] text-[#1f1f1f] text-xs font-nunito whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
                Support
              </span>
            </button>

            {/* Sign out */}
            <button
              onClick={handleSignOut}
              className="relative inline-flex items-center justify-center group"
              aria-label="Sign out"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                className="w-5 h-5"
              >
                <path
                  fillRule="evenodd"
                  d="M3 4.25A2.25 2.25 0 0 1 5.25 2h5.5A2.25 2.25 0 0 1 13 4.25v2a.75.75 0 0 1-1.5 0v-2a.75.75 0 0 0-.75-.75h-5.5a.75.75 0 0 0-.75.75v11.5c0 .414.336.75.75.75h5.5a.75.75 0 0 0 .75-.75v-2a.75.75 0 0 1 1.5 0v2A2.25 2.25 0 0 1 10.75 18h-5.5A2.25 2.25 0 0 1 3 15.75V4.25Z"
                  clipRule="evenodd"
                />
                <path
                  fillRule="evenodd"
                  d="M19 10a.75.75 0 0 0-.75-.75H8.704l1.048-.943a.75.75 0 1 0-1.004-1.114l-2.5 2.25a.75.75 0 0 0 0 1.114l2.5 2.25a.75.75 0 1 0 1.004-1.114l-1.048-.943h9.546A.75.75 0 0 0 19 10Z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 rounded-md bg-[#fefefe] text-[#1f1f1f] text-xs font-nunito whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
                Sign out
              </span>
            </button>
          </div>

          {/* User info */}
          {currentUser && (
            <div className="flex items-center gap-x-3">
              {currentUser.photoURL ? (
                <img
                  className="h-8 w-8 rounded-full object-cover"
                  src={currentUser.photoURL}
                  alt={currentUser.displayName || "User profile"}
                  referrerPolicy="no-referrer"
                />
              ) : (
                <div className="h-8 w-8 rounded-full bg-gray-600 flex items-center justify-center text-sm font-semibold">
                  {getInitial(currentUser)}
                </div>
              )}
              <div className="flex flex-col min-w-0">
                <div className="text-sm font-medium truncate">
                  {currentUser.displayName}
                </div>
                <div className="text-xs text-gray-400 truncate">
                  {currentUser.email}
                </div>
              </div>
            </div>
          )}
        </div>
      </aside>

      {/* Mobile backdrop */}
      <div
        className={`fixed inset-0 z-40 bg-black/50 md:hidden transition-opacity ${sidebarOpen ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        onClick={sidebarOpen ? toggleSidebar : undefined}
      />
    </>
  );
}
